// The day+shift bucket a delivery falls in, and the key that names it.
//
// It lives in the domain because two layers need the same answer: the
// repository buckets each ambassador's active deliveries by it, and the screen
// buckets the selection by it to show "Hoy AM: 3 → 5". If each derived its own
// the two halves of the assign modal would disagree.

import {
  SHIFT_BOUNDARY_MINUTES,
  type ShiftCode,
} from "../../../../shared/schedule/shifts";
import { toISODate } from "../../../../shared/schedule/calendar";

/** Opaque key for a day+shift pair: "2026-08-15|morning". */
export const slotKey = (dayISO: string, shift: ShiftCode): string => `${dayISO}|${shift}`;

/** Splits a key back into its parts. */
export const parseSlotKey = (key: string): { dayISO: string; shift: ShiftCode } => {
  const [dayISO, shift] = key.split("|");
  return { dayISO: dayISO ?? "", shift: (shift as ShiftCode) ?? "morning" };
};

/** The local day a slot falls on ("YYYY-MM-DD"), or null when unscheduled. */
export const dayOfSlot = (deliverySlotAt: string | null | undefined): string | null => {
  if (!deliverySlotAt) return null;
  const date = new Date(deliverySlotAt);
  return Number.isNaN(date.getTime()) ? null : toISODate(date);
};

/** The shift a delivery belongs to.
 *
 *  An agreed shift is authoritative. When there is none the delivery was
 *  promised at an exact hour, and the hour decides which half of the day it
 *  falls in — the supervisor filters by "mañana" and "tarde", and a 9:00
 *  delivery belongs in the morning whether or not anyone called it a shift.
 *  Null only when nothing is scheduled at all. */
export const shiftOfDelivery = (
  deliverySlotAt: string | null | undefined,
  deliveryShift: string | null | undefined,
): ShiftCode | null => {
  if (deliveryShift === "morning" || deliveryShift === "afternoon") return deliveryShift;
  if (!deliverySlotAt) return null;
  const date = new Date(deliverySlotAt);
  if (Number.isNaN(date.getTime())) return null;
  const minutes = date.getHours() * 60 + date.getMinutes();
  return minutes < SHIFT_BOUNDARY_MINUTES ? "morning" : "afternoon";
};

/** The bucket key for a delivery, or null when it carries no schedule. */
export const slotKeyOf = (
  deliverySlotAt: string | null | undefined,
  deliveryShift: string | null | undefined,
): string | null => {
  const day = dayOfSlot(deliverySlotAt);
  const shift = shiftOfDelivery(deliverySlotAt, deliveryShift);
  return day && shift ? slotKey(day, shift) : null;
};
